// A short buzz when a member ticks a course played or unticks it. Phones only.
//
// The two platforms need completely different things, and only one of them is a function call.
//
// Android and Chrome: navigator.vibrate, the real Vibration API. That is tapHaptic below.
//
// iOS and Safari: no navigator.vibrate at any version, including inside a home-screen app, and
// every browser on an iPhone is Safari underneath, so there is nothing to choose between them.
// The only haptic Safari will play is the one iOS plays for its own switch control, and it plays
// it only for a real finger landing on that switch. Tested on iOS 26:
//
//   tapping a switch by hand ............................. buzzed
//   tapping a label next to a switch ..................... buzzed
//   tapping an invisible switch laid over our own button . buzzed
//   tapping a switch with its native look stripped off ... buzzed
//   tapping a label whose switch is parked off screen .... buzzed
//   calling .click() on any of the above ................. silent
//
// So iOS haptics are not something a handler can ask for after the fact. The control the member
// touches has to BE a switch. IOS_SWITCH_PROPS below is how a control says it is one, and the
// last two rows above are why the switch can still look like whatever the design wants.

/** The two things a tick can mean. Each gets its own feel so they are told apart without looking. */
export type HapticKind = 'added' | 'removed';

// Milliseconds, as navigator.vibrate takes them. "added" is two quick taps, a small "got it";
// "removed" is one shorter tap, deliberately plainer, because taking something off your list
// should not feel like a reward. Kept short: anything over about 40ms reads as a phone alert
// rather than as button feedback.
const PATTERNS: Record<HapticKind, number[]> = {
  added: [18, 40, 18],
  removed: [14],
};

// Pointer, not screen width. A narrow desktop window is still a mouse, and a buzzing laptop
// trackpad is not what was asked for. matchMedia is guarded because it is absent in test
// environments (jsdom without a matchMedia polyfill).
const isTouchDevice = (): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }
  return window.matchMedia('(any-pointer: coarse)').matches;
};

// A member who has asked the OS for less motion has asked for less of this too. Vibration is not
// literally motion on screen, but it is the same "stop poking me" preference, and iOS and Android
// both surface it in the same accessibility screen.
//
// Note this only governs the Android path. The iOS haptic is played by the system itself, and
// the system already honours the member's own haptic settings without being asked.
const prefersReducedMotion = (): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Marks an <input type="checkbox"> as an iOS switch, which is what makes the system play its
 * haptic when a finger flips it. Spread onto the input:
 *
 *   <input type="checkbox" {...IOS_SWITCH_PROPS} checked={...} onChange={...} />
 *
 * Safari 17.4 and later act on it. Every other browser ignores an attribute it does not know, so
 * this is inert on Android and on the desktop rather than something to feature-detect.
 *
 * The cast is because `switch` is not in React's DOM typings yet. It is a plain HTML attribute
 * and React forwards unknown lowercase attributes to the DOM untouched, so the cast is a gap in
 * the types, not a trick.
 *
 * Two things it needs to work, both learned the hard way:
 * 1. The member's finger has to land on the input itself, or on a <label> tied to it. A
 *    programmatic .click() is silent.
 * 2. The input may be styled to look like anything, including nothing: appearance:none keeps the
 *    haptic, and so does an input at opacity 0 stretched over a control of our own.
 */
export const IOS_SWITCH_PROPS = { switch: '' } as unknown as Record<
  string,
  string
>;

/**
 * Buzz an Android phone for a tick. Safe to call from anywhere: on a desktop, on an iPhone, or on
 * a browser without the Vibration API, it does nothing at all.
 *
 * Call it from the click handler, not from an effect that watches the saved state. It has to run
 * inside the user gesture or the browser drops it, and the saved state also moves on a failed
 * save rolling back, which is not something to buzz about.
 *
 * This is deliberately NOT the whole story: on an iPhone it always does nothing, and the haptic
 * comes from IOS_SWITCH_PROPS on the control instead.
 */
export const tapHaptic = (kind: HapticKind): void => {
  if (!isTouchDevice() || prefersReducedMotion()) {
    return;
  }

  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.vibrate === 'function'
    ) {
      navigator.vibrate(PATTERNS[kind]);
    }
  } catch {
    // Vibration can be blocked by the OS, by a battery saver, or by a permissions policy on the
    // page. None of that is worth an error in the console, let alone a broken tick.
  }
};
